import React, { useMemo } from "react"
import { Container, Row, Col } from "reactstrap"
import { useNavigate } from "react-router-dom"
import ReactApexChart from "react-apexcharts"
import { useChartFix } from "../dashkit"
import useCollapsedSidebar from "../../../hooks/useCollapsedSidebar"

/*
 * Owner Dashboard — group/company overview landing (from Anil's mockup, our theme).
 * STATIC data for now: the numbers/series below are placeholders. The module tiles
 * navigate to the real module landings; the header reflects the real logged-in user
 * and the scope picked in the top-bar company switcher (company_context).
 * Wire real figures later behind a /dashboard/owner-summary endpoint (per scope).
 */

// ---- helpers -------------------------------------------------------------
const CUR = "AED"
const fmtM = n => `${CUR} ${(n / 1_000_000).toLocaleString("en-AE", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}M`
const fmtN = n => n.toLocaleString("en-AE")

const authUser = (() => {
  try { return JSON.parse(localStorage.getItem("authUser") || "{}") } catch { return {} }
})()
const ctx = authUser?.company_context || {}

// palette (one accent per KPI, matches the mock's coloured sparklines)
const C = {
  revenue: "#4f6ef7",
  expense: "#22c55e",
  profit: "#8b5cf6",
  cash: "#f59e0b",
  people: "#06b6d4",
  grid: "#eef1f6",
  text: "#0f172a",
  sub: "#64748b",
}

const sparkline = (data, color) => ({
  chart: { type: "area", sparkline: { enabled: true }, height: 56 },
  stroke: { curve: "smooth", width: 2.5 },
  fill: {
    type: "gradient",
    gradient: { shadeIntensity: 1, opacityFrom: 0.35, opacityTo: 0.02, stops: [0, 100] },
  },
  colors: [color],
  tooltip: { enabled: false },
  series: [{ data }],
})

// ---- static content ------------------------------------------------------
const KPIS = [
  { key: "revenue", label: "Total Revenue", value: fmtM(24_750_000), delta: "+18.6%", up: true, color: C.revenue, spark: [8, 10, 9, 12, 11, 14, 13, 16, 18, 20, 22, 24] },
  { key: "expense", label: "Total Expenses", value: fmtM(16_350_000), delta: "+12.4%", up: true, color: C.expense, spark: [6, 7, 7, 9, 8, 10, 11, 12, 13, 14, 15, 16] },
  { key: "profit", label: "Net Profit", value: fmtM(8_400_000), delta: "+27.8%", up: true, color: C.profit, spark: [2, 3, 3, 4, 4, 5, 5, 6, 6, 7, 8, 8] },
  { key: "cash", label: "Cash & Bank Balance", value: fmtM(11_320_000), delta: "+15.3%", up: true, color: C.cash, spark: [7, 8, 8, 9, 9, 10, 10, 11, 11, 11, 11, 11] },
  { key: "people", label: "Total Employees", value: fmtN(458), delta: "+8.2%", up: true, color: C.people, spark: [380, 395, 402, 410, 420, 428, 435, 440, 448, 452, 455, 458] },
]

const MODULES = [
  { label: "HRMS", icon: "bx-group", stat: "458", sub: "Total Employees", route: "/hrms", tint: "#eef2ff", fg: "#4f6ef7" },
  { label: "Project Management", icon: "bx-briefcase-alt-2", stat: "32", sub: "Active Projects", route: "/project", tint: "#ecfdf5", fg: "#22c55e" },
  { label: "Procurements", icon: "bx-cart", stat: fmtM(9_840_000), sub: "Total Purchases", route: "/procurement", tint: "#fff7ed", fg: "#f59e0b" },
  { label: "Accounts", icon: "bx-receipt", stat: fmtM(24_750_000), sub: "Total Revenue", route: "/finance-dashboard", tint: "#eef2ff", fg: "#6366f1" },
  { label: "Camp", icon: "bx-tent", stat: "12", sub: "Active Camps", route: "/camp", tint: "#fef2f2", fg: "#ef4444" },
  { label: "Fleet", icon: "bx-car", stat: "86", sub: "Total Vehicles", route: "/fleet", tint: "#f0f9ff", fg: "#0ea5e9" },
  { label: "Inventory", icon: "bx-box", stat: fmtM(7_650_000), sub: "Total Stock Value", route: "/central-store", tint: "#f5f3ff", fg: "#8b5cf6" },
  { label: "Staff Turnover", icon: "bx-transfer-alt", stat: "4.2%", sub: "Annual Turnover", route: "/hrms", tint: "#f0fdfa", fg: "#14b8a6" },
]

// group companies — use real names from company_context if present, else sample
const REAL_COMPANIES = (ctx?.companies || []).map(c => c.company_name || c.code).filter(Boolean)
const SAMPLE = ["ABC Construction Pvt Ltd", "ABC Infratech Pvt Ltd", "ABC Trading Pvt Ltd", "ABC Properties Pvt Ltd", "ABC Logistics Pvt Ltd", "ABC Services Pvt Ltd", "ABC Holdings Pvt Ltd"]
const COMPANY_NAMES = (REAL_COMPANIES.length ? REAL_COMPANIES : SAMPLE).slice(0, 7)
const COMPANY_VALS = [12_450_000, 8_320_000, 5_250_000, 3_150_000, 2_450_000, 1_850_000, 1_280_000].slice(0, COMPANY_NAMES.length)
const DONUT_COLORS = ["#4f6ef7", "#22c55e", "#f59e0b", "#8b5cf6", "#06b6d4", "#ef4444", "#64748b"]

const TOP_PROJECTS = [
  { name: "Highway Construction", co: "ABC Construction Pvt Ltd", pct: 75, budget: fmtM(42_500_000).replace(`${CUR} `, `${CUR} `), status: "On Track" },
  { name: "Smart City Project", co: "ABC Infratech Pvt Ltd", pct: 60, budget: fmtM(36_000_000), status: "On Track" },
  { name: "Residential Complex", co: "ABC Properties Pvt Ltd", pct: 40, budget: fmtM(28_000_000), status: "At Risk" },
  { name: "Warehouse Construction", co: "ABC Construction Pvt Ltd", pct: 20, budget: fmtM(17_500_000), status: "Delayed" },
]
const STATUS_STYLE = {
  "On Track": { bg: "#ecfdf5", fg: "#16a34a" },
  "At Risk": { bg: "#fffbeb", fg: "#d97706" },
  Delayed: { bg: "#fef2f2", fg: "#dc2626" },
}

const ALERTS = [
  { icon: "bx-error-circle", fg: "#dc2626", bg: "#fef2f2", title: "3 Projects are Overdue", sub: "Highway Construction, Smart City Project…", ago: "10 min ago" },
  { icon: "bx-receipt", fg: "#d97706", bg: "#fffbeb", title: "12 Purchase Orders Awaiting Approval", sub: `Total Amount: ${fmtM(2_450_000)}`, ago: "30 min ago" },
  { icon: "bx-car", fg: "#0ea5e9", bg: "#f0f9ff", title: "Vehicle Maintenance Due", sub: "8 Vehicles require immediate attention", ago: "1 hr ago" },
  { icon: "bx-box", fg: "#8b5cf6", bg: "#f5f3ff", title: "Low Stock Alert", sub: "24 Items are below minimum stock level", ago: "2 hr ago" },
]

const MONTHS = ["Dec 2023", "Jan 2024", "Feb 2024", "Mar 2024", "Apr 2024", "May 2024"]

// ---- component -----------------------------------------------------------
const OwnerDashboard = () => {
  const navigate = useNavigate()
  document.title = "Owner Dashboard | Calyvern"
  useChartFix()
  useCollapsedSidebar()

  const userName = authUser?.name || "Admin"

  const trend = useMemo(() => ({
    options: {
      chart: { type: "bar", toolbar: { show: false }, fontFamily: "inherit" },
      plotOptions: { bar: { columnWidth: "55%", borderRadius: 4 } },
      dataLabels: { enabled: false },
      colors: [C.revenue, C.expense],
      xaxis: { categories: MONTHS, axisBorder: { show: false }, axisTicks: { show: false }, labels: { style: { colors: C.sub } } },
      yaxis: { labels: { formatter: v => `${(v).toFixed(0)}M`, style: { colors: C.sub } } },
      grid: { borderColor: C.grid, strokeDashArray: 4 },
      legend: { show: false },
      tooltip: { y: { formatter: v => `${CUR} ${v.toFixed(1)}M` } },
    },
    series: [
      { name: "Revenue", data: [18, 22, 21, 25, 24, 27] },
      { name: "Expenses", data: [11, 12, 13, 14, 15, 16] },
    ],
  }), [])

  const donut = useMemo(() => ({
    options: {
      chart: { type: "donut", fontFamily: "inherit" },
      labels: COMPANY_NAMES,
      colors: DONUT_COLORS.slice(0, COMPANY_NAMES.length),
      legend: { show: false },
      dataLabels: { enabled: false },
      stroke: { width: 0 },
      plotOptions: {
        pie: {
          donut: {
            size: "72%",
            labels: {
              show: true,
              total: { show: true, label: "Companies", fontSize: "13px", color: C.sub, formatter: () => `${COMPANY_NAMES.length}` },
              value: { fontSize: "26px", fontWeight: 700, color: C.text },
            },
          },
        },
      },
      tooltip: { y: { formatter: v => fmtM(v * 1_000_000) } },
    },
    series: COMPANY_VALS.map(v => +(v / 1_000_000).toFixed(2)),
  }), [])

  return (
    <React.Fragment>
      <style>{ownerCss}</style>
      <div className="page-content od-wrap">
        <Container fluid>
          {/* Header */}
          <div className="od-head">
            <div>
              <h4 className="od-title">Welcome {userName}</h4>
            </div>
            <div className="od-daterange">
              <i className="bx bx-calendar" /> 01 May 2024 – 31 May 2024 <i className="bx bx-chevron-down" />
            </div>
          </div>

          {/* KPI cards */}
          <Row className="g-3 od-kpis">
            {KPIS.map(k => (
              <Col key={k.key} xl className="col-kpi">
                <div className="od-card od-kpi">
                  <div className="od-kpi-top">
                    <span className="od-kpi-label">{k.label}</span>
                  </div>
                  <div className="od-kpi-value">{k.value}</div>
                  <div className={`od-kpi-delta ${k.up ? "up" : "down"}`}>
                    <i className={`bx ${k.up ? "bx-up-arrow-alt" : "bx-down-arrow-alt"}`} />{k.delta} vs Apr 2024
                  </div>
                  <div className="od-kpi-spark">
                    <ReactApexChart options={sparkline(k.spark, k.color)} series={[{ data: k.spark }]} type="area" height={56} />
                  </div>
                </div>
              </Col>
            ))}
          </Row>

          {/* Module overview + group companies */}
          <Row className="g-3 mt-1">
            <Col xxl={8}>
              <div className="od-card h-100">
                <div className="od-card-head">
                  <span>MODULE OVERVIEW</span>
                  <button className="od-link" onClick={() => navigate("/modules")}>View All Modules →</button>
                </div>
                <div className="od-modules">
                  {MODULES.map(m => (
                    <div key={m.label} className="od-mod" role="button" onClick={() => navigate(m.route)}>
                      <span className="od-mod-icon" style={{ background: m.tint, color: m.fg }}><i className={`bx ${m.icon}`} /></span>
                      <div className="od-mod-label">{m.label}</div>
                      <div className="od-mod-stat">{m.stat}</div>
                      <div className="od-mod-sub">{m.sub}</div>
                      <div className="od-mod-link" style={{ color: m.fg }}>View Details →</div>
                    </div>
                  ))}
                </div>
              </div>
            </Col>
            <Col xxl={4}>
              <div className="od-card h-100">
                <div className="od-card-head">
                  <span>GROUP COMPANY OVERVIEW</span>
                  <button className="od-link" onClick={() => navigate("/company-management")}>View All →</button>
                </div>
                <div className="od-group">
                  <div className="od-donut">
                    <ReactApexChart options={donut.options} series={donut.series} type="donut" height={220} />
                  </div>
                  <ul className="od-colist">
                    {COMPANY_NAMES.map((n, i) => (
                      <li key={n}>
                        <span className="od-dot" style={{ background: DONUT_COLORS[i] }} />
                        <span className="od-co-name" title={n}>{n}</span>
                        <span className="od-co-val">{fmtM(COMPANY_VALS[i])}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </Col>
          </Row>

          {/* Trend + top projects + alerts */}
          <Row className="g-3 mt-1">
            <Col xxl={5}>
              <div className="od-card h-100">
                <div className="od-card-head">
                  <span>REVENUE &amp; EXPENSE TREND</span>
                  <span className="od-chip">Last 6 Months <i className="bx bx-chevron-down" /></span>
                </div>
                <div className="od-legend">
                  <span><span className="od-dot" style={{ background: C.revenue }} /> Revenue</span>
                  <span><span className="od-dot" style={{ background: C.expense }} /> Expenses</span>
                </div>
                <ReactApexChart options={trend.options} series={trend.series} type="bar" height={260} />
              </div>
            </Col>
            <Col xxl={4}>
              <div className="od-card h-100">
                <div className="od-card-head"><span>TOP PROJECTS</span><button className="od-link" onClick={() => navigate("/project")}>View All →</button></div>
                <table className="od-table">
                  <thead><tr><th>Project</th><th>Progress</th><th>Budget</th><th>Status</th></tr></thead>
                  <tbody>
                    {TOP_PROJECTS.map(p => (
                      <tr key={p.name}>
                        <td><div className="od-p-name">{p.name}</div><div className="od-p-co">{p.co}</div></td>
                        <td>
                          <div className="od-progress"><span style={{ width: `${p.pct}%`, background: STATUS_STYLE[p.status].fg }} /></div>
                          <div className="od-p-pct">{p.pct}%</div>
                        </td>
                        <td className="od-p-budget">{p.budget}</td>
                        <td><span className="od-badge" style={{ background: STATUS_STYLE[p.status].bg, color: STATUS_STYLE[p.status].fg }}>{p.status}</span></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Col>
            <Col xxl={3}>
              <div className="od-card h-100">
                <div className="od-card-head"><span>Notifications</span><button className="od-link" onClick={() => navigate("/notification")}>View All →</button></div>
                <ul className="od-alerts">
                  {ALERTS.map(a => (
                    <li key={a.title}>
                      <span className="od-alert-icon" style={{ background: a.bg, color: a.fg }}><i className={`bx ${a.icon}`} /></span>
                      <div className="od-alert-body">
                        <div className="od-alert-title">{a.title}</div>
                        <div className="od-alert-sub">{a.sub}</div>
                      </div>
                      <span className="od-alert-ago">{a.ago}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </Col>
          </Row>
        </Container>
      </div>
    </React.Fragment>
  )
}

// scoped styles (kept local so we don't touch global scss)
const ownerCss = `
.od-wrap{background:#f6f7fb;min-height:100%;}
.od-head{display:flex;justify-content:space-between;align-items:flex-start;gap:16px;flex-wrap:wrap;margin-bottom:18px;}
.od-title{font-weight:700;color:#0f172a;margin:0;display:flex;align-items:center;gap:8px;font-size:22px;}
.od-title .bxs-check-circle{color:#4f6ef7;font-size:18px;}
.od-sub{color:#64748b;font-size:13.5px;margin-top:2px;}
.od-daterange{display:flex;align-items:center;gap:8px;background:#fff;border:1px solid #e5e7eb;border-radius:10px;padding:8px 14px;font-size:13px;color:#334155;font-weight:600;}
.od-card{background:#fff;border:1px solid #eef0f4;border-radius:16px;padding:18px;box-shadow:0 1px 2px rgba(16,24,40,.04);}
.od-card-head{display:flex;justify-content:space-between;align-items:center;margin-bottom:14px;}
.od-card-head>span:first-child{font-size:12px;font-weight:700;letter-spacing:.05em;color:#334155;}
.od-link{border:none;background:none;color:#4f6ef7;font-weight:600;font-size:12.5px;cursor:pointer;padding:0;}
.od-chip{font-size:12px;color:#64748b;background:#f1f5f9;border-radius:8px;padding:4px 10px;font-weight:600;}
/* KPI */
.od-kpis .col-kpi{min-width:200px;}
.od-kpi{position:relative;overflow:hidden;}
.od-kpi-label{font-size:12px;font-weight:600;color:#64748b;text-transform:uppercase;letter-spacing:.03em;}
.od-kpi-value{font-size:22px;font-weight:700;color:#0f172a;margin-top:8px;}
.od-kpi-delta{font-size:12px;font-weight:600;margin-top:4px;display:flex;align-items:center;gap:3px;}
.od-kpi-delta.up{color:#16a34a;} .od-kpi-delta.down{color:#dc2626;}
.od-kpi-spark{margin:6px -6px -8px;}
/* modules */
.od-modules{display:grid;grid-template-columns:repeat(4,1fr);gap:12px;}
.od-mod{border:1px solid #eef0f4;border-radius:14px;padding:14px;cursor:pointer;transition:.15s;background:#fff;}
.od-mod:hover{border-color:#c7d2fe;box-shadow:0 6px 18px rgba(79,110,247,.10);transform:translateY(-2px);}
.od-mod-icon{width:42px;height:42px;border-radius:12px;display:flex;align-items:center;justify-content:center;font-size:22px;margin-bottom:10px;}
.od-mod-label{font-size:12.5px;color:#64748b;font-weight:600;}
.od-mod-stat{font-size:19px;font-weight:700;color:#0f172a;margin-top:2px;}
.od-mod-sub{font-size:11.5px;color:#94a3b8;}
.od-mod-link{font-size:12px;font-weight:600;margin-top:10px;}
/* group */
.od-group{display:flex;flex-direction:column;gap:6px;}
.od-colist{list-style:none;padding:0;margin:8px 0 0;}
.od-colist li{display:flex;align-items:center;gap:8px;padding:6px 0;font-size:12.5px;}
.od-dot{width:9px;height:9px;border-radius:50%;flex:0 0 auto;display:inline-block;}
.od-co-name{color:#334155;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;flex:1;}
.od-co-val{color:#0f172a;font-weight:700;white-space:nowrap;}
/* legend */
.od-legend{display:flex;gap:16px;font-size:12.5px;color:#64748b;margin-bottom:4px;}
.od-legend span{display:flex;align-items:center;gap:6px;}
/* table */
.od-table{width:100%;border-collapse:collapse;font-size:12.5px;}
.od-table th{text-align:left;color:#94a3b8;font-weight:600;font-size:11.5px;padding:6px 8px;border-bottom:1px solid #eef0f4;}
.od-table td{padding:10px 8px;border-bottom:1px solid #f4f6f9;vertical-align:middle;}
.od-p-name{font-weight:600;color:#0f172a;}
.od-p-co{color:#94a3b8;font-size:11px;}
.od-progress{height:6px;background:#eef0f4;border-radius:6px;overflow:hidden;width:74px;}
.od-progress span{display:block;height:100%;border-radius:6px;}
.od-p-pct{font-size:11px;color:#64748b;margin-top:3px;}
.od-p-budget{font-weight:600;color:#0f172a;white-space:nowrap;}
.od-badge{font-size:11px;font-weight:600;padding:3px 10px;border-radius:20px;white-space:nowrap;}
/* alerts */
.od-alerts{list-style:none;padding:0;margin:0;}
.od-alerts li{display:flex;gap:10px;align-items:flex-start;padding:10px 0;border-bottom:1px solid #f4f6f9;}
.od-alerts li:last-child{border-bottom:none;}
.od-alert-icon{width:34px;height:34px;border-radius:10px;display:flex;align-items:center;justify-content:center;font-size:18px;flex:0 0 auto;}
.od-alert-body{flex:1;min-width:0;}
.od-alert-title{font-size:12.5px;font-weight:600;color:#0f172a;}
.od-alert-sub{font-size:11.5px;color:#94a3b8;}
.od-alert-ago{font-size:11px;color:#cbd5e1;white-space:nowrap;}
@media(max-width:1200px){.od-modules{grid-template-columns:repeat(3,1fr);}}
@media(max-width:768px){.od-modules{grid-template-columns:repeat(2,1fr);}}
`

export default OwnerDashboard
